import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as ai from "../../services/aiAssistantService";
import { articleStatus } from "./aiHelpers";

const STATUS_BADGE = {
  on: { cls: "text-bg-success", text: "On" },
  off: { cls: "text-bg-secondary", text: "Off" },
  "needs-answer": { cls: "text-bg-warning", text: "Needs an answer" }
};

const EMPTY = { id: null, title: "", body: "", tags: "", active: true };

// The answers the assistant may quote. It never makes up a policy: with nothing here that matches, it says
// it is not sure and offers a person. So an article is only used while it is On and has an answer.
const FaqTab = () => {
  const [articles, setArticles] = useState(null);
  const [topics, setTopics] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [list, starter] = await Promise.all([ai.getKb(), ai.getStarterTopics().catch(() => [])]);
      setArticles(list);
      setTopics(starter);
      setError(null);
    } catch (e) {
      setError((e && e.message) || "Could not load the FAQ");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const hintFor = useMemo(() => {
    const byTitle = new Map(topics.map((t) => [t.title.toLowerCase(), t.hint]));
    return (title) => byTitle.get(String(title || "").toLowerCase()) || "";
  }, [topics]);

  const importStarter = async () => {
    setBusy(true);
    const res = await ai.importStarter();
    setBusy(false);
    if (res && res.success) {
      toast.success(res.data && res.data.added ? `Added ${res.data.added} topics to fill in` : "All starter topics are already there");
      load();
    } else {
      toast.error((res && res.message) || "Could not add the topics");
    }
  };

  const save = async () => {
    setBusy(true);
    const body = { title: editing.title, body: editing.body, tags: editing.tags, active: editing.active && !!editing.body.trim() };
    const res = editing.id ? await ai.updateArticle(editing.id, body) : await ai.createArticle(body);
    setBusy(false);
    if (res && res.success) {
      toast.success("Saved");
      setEditing(null);
      load();
    } else {
      toast.error((res && res.message) || "Could not save");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    const res = await ai.deleteArticle(a.id);
    if (res && res.success) {
      toast.success("Deleted");
      load();
    } else {
      toast.error((res && res.message) || "Could not delete");
    }
  };

  const toggle = async (a) => {
    const res = await ai.updateArticle(a.id, { active: !a.active });
    if (res && res.success) load();
    else toast.error((res && res.message) || "Could not change it");
  };

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!articles) return <div className="text-muted py-4">Loading…</div>;

  const needAnswer = articles.filter((a) => !a.body).length;

  return (
    <>
      <div className="card shadow-sm rounded-4 mb-3">
        <div className="card-body d-flex flex-wrap gap-2 align-items-center">
          <div className="me-auto">
            <h6 className="fw-semibold mb-1">Answers the assistant may use</h6>
            <p className="text-muted small mb-0">
              Write each answer in plain English, the way you would tell a customer; the assistant translates it. It only uses articles that are On, and
              it never invents a policy: if nothing here matches, it says it is not sure and offers a person.
            </p>
          </div>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={importStarter} disabled={busy}>
            Add starter topics
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing({ ...EMPTY })}>
            New article
          </button>
        </div>
      </div>

      {needAnswer > 0 && (
        <div className="alert alert-warning small">
          {needAnswer} {needAnswer === 1 ? "article needs" : "articles need"} an answer before the assistant can use {needAnswer === 1 ? "it" : "them"}.
        </div>
      )}

      {articles.length === 0 ? (
        <div className="text-center text-muted py-5 small">No articles yet. Use "Add starter topics" for a list of the usual questions to fill in.</div>
      ) : (
        <div className="list-group shadow-sm rounded-4">
          {articles.map((a) => {
            const st = STATUS_BADGE[articleStatus(a)];
            return (
              <div className="list-group-item d-flex align-items-center gap-3" key={a.id}>
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="fw-semibold text-truncate">{a.title}</div>
                  <div className="text-muted small text-truncate">{a.body || hintFor(a.title) || "No answer yet"}</div>
                </div>
                <span className={`badge ${st.cls}`} style={{ fontSize: 10 }}>
                  {st.text}
                </span>
                <div className="d-flex gap-1">
                  {a.body && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => toggle(a)}>
                      {a.active ? "Turn off" : "Turn on"}
                    </button>
                  )}
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setEditing({ id: a.id, title: a.title, body: a.body || "", tags: a.tags || "", active: !!a.active })}>
                    {a.body ? "Edit" : "Write answer"}
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(a)} aria-label={`Delete ${a.title}`}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="modal d-block" tabIndex={-1} role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">{editing.id ? "Edit article" : "New article"}</h6>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setEditing(null)} />
              </div>
              <div className="modal-body">
                <label className="form-label small mb-1" htmlFor="faq-title">
                  Question or topic
                </label>
                <input id="faq-title" className="form-control mb-3" maxLength={200} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                <label className="form-label small mb-1" htmlFor="faq-body">
                  Answer
                </label>
                {hintFor(editing.title) && <div className="text-muted small mb-1">{hintFor(editing.title)}</div>}
                <textarea id="faq-body" className="form-control mb-1" rows={7} maxLength={4000} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
                <div className="text-muted small mb-3">{editing.body.length} / 4000</div>
                <label className="form-label small mb-1" htmlFor="faq-tags">
                  Words customers use <span className="text-muted">(optional, helps the assistant find it)</span>
                </label>
                <input id="faq-tags" className="form-control mb-3" maxLength={400} value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} />
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="faq-active"
                    checked={editing.active && !!editing.body.trim()}
                    disabled={!editing.body.trim()}
                    onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="faq-active">
                    The assistant may use this answer
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link" onClick={() => setEditing(null)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !editing.title.trim()}>
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FaqTab;
